import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { FilterOperator } from "features/ui/Filters/types";
import {
  areFilterValuesComplete,
  getFilterStateCount,
} from "features/ui/Filters/utils";

import {
  getFilterGroupStateTopLevelRowAttributes,
  getTopLevelRowFromFilterGroupState,
} from "./filterState";

/**
 * Filter state is valid when:
 * - it has at least one child
 * - all children have attribute, operator, and values set
 * - no IDs are duplicated
 * - operator is not "not_filtered"
 * - values are complete for the operator ("between" needs both bounds)
 * - all the nested groups pass the above conditions too
 */
export const isFilterBuilderStateValid = (
  groupState?: FilterGroupState,
  allowNotFilteredOperator = false,
  level = 0
): boolean => {
  if (!groupState || !groupState.children) return false;

  if (groupState.children.length === 0) return level === 0;

  const ids = new Set<string>();

  return groupState.children.every((child) => {
    // id must be unique
    if (ids.has(child.id)) {
      console.warn("Duplicate id found", child.id);

      return false;
    }

    ids.add(child.id);

    if (child.type === "group") {
      return isFilterBuilderStateValid(
        child as FilterGroupState,
        allowNotFilteredOperator,
        level + 1
      );
    }

    const { attribute, operator, values } = child as FilterRowState;

    return (
      !!attribute &&
      !!operator &&
      ((allowNotFilteredOperator && operator === FilterOperator.NOT_FILTERED) ||
        (operator !== FilterOperator.NOT_FILTERED &&
          Array.isArray(values) &&
          areFilterValuesComplete(operator, values) &&
          ([FilterOperator.OCCURS, FilterOperator.NOT_OCCURS].includes(operator)
            ? isValidOccursFilterValue(values)
            : true)))
    );
  });
};

const isValidOccursFilterValue = (values: string[]): boolean => {
  if (values.length !== 1) return false;

  const value = values[0];

  // check structure (allows nested): {window params}[filter conditions]
  const matches = value.match(
    /^{([^}]+)}(\[(?:[^[\]]|\[(?:[^[\]]|\[[^[\]]*\])*\])*\])$/
  );
  if (!matches) return false;

  const [, , filterConditions] = matches;

  // Validate filter conditions - should not be empty between [] brackets
  const filterContent = filterConditions.slice(1, -1);

  return filterContent.trim() !== "";
};

// if it has || or ( or ) but NOT within "" then it is an advanced filter
export const isAdvancedFilter = (filter: string): boolean =>
  /(\|\||\(|\))(?=(?:[^"]|"[^"]*")*$)/.test(filter.trim());

export const isAdvancedFilterState = (filter: FilterGroupState): boolean => {
  // if top-level group is "any" then it's advanced
  // - but only if there are more than one children
  if (filter.anyAll === "any" && filter.children.length > 1) return true;

  // no other groups are expected
  if (filter.children.some((child) => child.type === "group")) return true;

  if (
    filter.children.some(
      (child) =>
        child.type === "row" &&
        hasEmbeddedAdvancedFilters(child as FilterRowState)
    )
  ) {
    return true;
  }

  // every attribute has to appear only once
  const attributes = getFilterGroupStateTopLevelRowAttributes(filter);
  if (new Set(attributes).size !== attributes.length) {
    return true;
  }

  return false;
};

const hasEmbeddedAdvancedFilters = ({ values, operator }: FilterRowState) => {
  if (!values || values.length === 0 || !operator) return false;

  if ([FilterOperator.EXISTS, FilterOperator.NOT_EXISTS].includes(operator)) {
    return true;
  }

  if ([FilterOperator.OCCURS, FilterOperator.NOT_OCCURS].includes(operator)) {
    return true;
  }

  return false;
};

export const areFiltersEqual = (
  filters1?: FilterGroupState,
  filters2?: FilterGroupState
) => {
  if (!filters1 || !filters2) return false;

  if (getFilterStateCount(filters1) !== getFilterStateCount(filters2)) {
    return false;
  }

  for (const key of getFilterGroupStateTopLevelRowAttributes(filters1)) {
    const rowFromFilters2 = getTopLevelRowFromFilterGroupState(key, filters2);
    const rowFromFilters1 = getTopLevelRowFromFilterGroupState(key, filters1);

    if (!rowFromFilters2) {
      return false;
    }

    if (rowFromFilters1?.operator !== rowFromFilters2?.operator) {
      return false;
    }

    if (
      rowFromFilters1?.values?.slice().sort().join("") !==
      rowFromFilters2?.values?.slice().sort().join("")
    ) {
      return false;
    }
  }

  return true;
};

export const areFilterStatesEqual = (
  objA: FilterGroupState,
  objB: FilterGroupState
): boolean => {
  if (objA === objB) return true;

  if (!objA || !objB) return false;

  if (objA.anyAll !== objB.anyAll) return false;

  if (objA.children.length !== objB.children.length) return false;

  return objA.children.every((child, index) => {
    if (child.type === "group") {
      return areFilterStatesEqual(
        child as FilterGroupState,
        objB.children[index] as FilterGroupState
      );
    }

    const rowA = child as FilterRowState;
    const rowB = objB.children[index] as FilterRowState;

    // .slice() used so we dont mutate original values order!
    const sortedValuesA = rowA?.values?.slice().sort();
    const sortedValuesB = rowB?.values?.slice().sort();

    return (
      rowA.attribute === rowB.attribute &&
      rowA.operator === rowB.operator &&
      rowA.values?.length === rowB.values?.length &&
      sortedValuesB &&
      sortedValuesA?.every((val, i) => val === sortedValuesB[i])
    );
  });
};
